import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import { Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'
import { useNewTranslation } from 'hooks'
import RectangleButton from 'particles/Button/RectangleButton'
import { login } from 'state/profile'
import { createGetRequestErrors, selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { checkForError } from 'services/api/errors'
import { push } from 'connected-react-router'
import { RegisterActions } from 'services/api/register'
import { AlertContentType } from 'particles/Alert'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import Text from 'particles/Text'
import InputField from 'components/Shared/Fields'
import Checkbox from 'particles/Checkbox'
import styled from 'particles/themes/styled'

interface FormValues {
    email: string
    password: string
}

const LoginForm: React.FC = () => {
    const [alertConfig, setAlertConfig] = useState<{
        show: boolean
        configType: AlertContentType
        successTitle: string
        successSubtext: string
        reminderAction?: {
            label: string
            callback: () => void
        }
    }>({
        show: false,
        configType: 'positive',
        successTitle: '',
        successSubtext: '',
    })
    const dispatch = useDispatch()
    const location = useLocation()
    const getErrors = createGetRequestErrors([STATE_KEYS.Login])
    const errors = useSelector(getErrors)[STATE_KEYS.Login]
    const params = qs.parse(location.search)
    const { activated } = params
    const formikRef = useRef<FormikProps<FormValues>>(null)
    const isCredentialError = checkForError(errors, 'LOGIN_INVALID', 'INVALID_CREDENTIALS')
    const isEmailUsedForAppError = checkForError(errors, 'LOGIN_INVALID', 'EMAIL_ALREADY_IN_USED_FOR_APP')
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.Login]))
    const [showPWD, setShowPWD] = useState(false)

    const [t] = useNewTranslation()

    useEffect(() => {
        if (isCredentialError) {
            setAlertConfig({
                ...alertConfig,
                show: true,
                configType: 'error',
                successTitle: t('errorMessage.general.incorrectEmailPassword'),
            })
        }
    }, [isCredentialError])

    useEffect(() => {
        if (isEmailUsedForAppError) {
            setAlertConfig({
                ...alertConfig,
                show: true,
                configType: 'error',
                successTitle: t('logIn.resetPassword.emailErrorMessage.hasRegisterApp'),
            })
        }
    }, [isEmailUsedForAppError])

    useEffect(() => {
        if (activated) {
            setAlertConfig({
                ...alertConfig,
                show: true,
                successTitle: t('successMessage.logIn.accountActivatedTitle'),
                successSubtext: t('successMessage.logIn.accountActivatedSubText'),
            })
        }
    }, [activated])

    useEffect(() => {
        if (errors && !isCredentialError && !isEmailUsedForAppError) {
            const isNotActivatedError = errors?.general_errors?.params?.details === 'USER_NOT_ACTIVATED'
            setAlertConfig({
                ...alertConfig,
                show: true,
                configType: isNotActivatedError ? 'info' : 'error',
                successTitle: isNotActivatedError
                    ? t('logIn.signUp.activateEmailTitle', { 'user email': formikRef.current?.values.email })
                    : t('errorMessage.general.somethingWentWrongTitle'),
                successSubtext: isNotActivatedError ? '' : t('errorMessage.signUp.refreshScreen'),
                reminderAction: isNotActivatedError
                    ? {
                          label: t('button.sendLinkAgain'),
                          callback: () =>
                              dispatch({
                                  type: RegisterActions.SEND_INVITE,
                                  payload: { email: formikRef.current?.values.email },
                              }),
                      }
                    : undefined,
            })
        }
    }, [errors, isCredentialError])

    return (
        <Formik
            initialValues={{
                email: '',
                password: '',
            }}
            validationSchema={yup.object().shape({
                email: yup
                    .string()
                    .email(t('errorMessage.validation.enterValidEmail', { email: 'name@example.com' }))
                    .required(t('errorMessage.validation.enterEmailAddress')),
                password: yup
                    .string()
                    .min(8, t('errorMessage.businessLogic.passwordEightCharacters'))
                    .required(t('errorMessage.validation.enterPassword')),
            })}
            innerRef={formikRef}
            onSubmit={values => {
                if (!isLoading) {
                    const { email, password } = values
                    dispatch(login({ email, password }))
                }
            }}
        >
            {() => (
                <Form>
                    {alertConfig.show && (
                        <ReminderAlert
                            form={alertConfig?.configType}
                            title={alertConfig?.successTitle}
                            subtext={alertConfig?.successSubtext}
                            action={alertConfig?.reminderAction}
                            isClosable={false}
                            width="100%"
                        />
                    )}
                    <div className="auth-form">
                        <InputField.TextInputField
                            name="email"
                            type="email"
                            label={t('logIn.resetPassword.emailTitle')}
                        />
                        <InputField.TextInputField
                            name="password"
                            type={showPWD ? 'text' : 'password'}
                            label={t('logIn.homepage.enterPassword')}
                            data-private
                        />
                        <Checkbox
                            label={t('logIn.homepage.showPassword')}
                            onChange={() => {
                                setShowPWD(prevValue => !prevValue)
                            }}
                            checkbox={showPWD ? 'selected' : 'notSelected'}
                        />
                    </div>

                    <RectangleButton
                        type="submit"
                        isDisabled={isLoading}
                        label={t('button.logIn')}
                        size="l"
                        style={{ width: '100%' }}
                    />
                </Form>
            )}
        </Formik>
    )
}

const Login: React.FC<any> = ({ className }) => {
    const dispatch = useDispatch()

    const [t] = useNewTranslation()

    return (
        <div className={className}>
            <div className="block">
                <Text scale="H2">{t('logIn.logInTitle')}</Text>
            </div>
            <div className="block">
                <LoginForm />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Text>{t('button.forgotPassword')}</Text>
                    <RectangleButton
                        label={t('logIn.resetPasswordHeader')}
                        form="text"
                        size="s"
                        color="neutral"
                        onClick={() => dispatch(push('/reset-password'))}
                    />
                </div>
            </div>
            <Text scale="caption400" className="copyright">
                {t('logIn.partipostCopyrightFooter')}
            </Text>
        </div>
    )
}

export default styled(Login)`
    max-width: 520px;
    width: 520px;
    min-width: 343px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: fit-content;
    max-height: 74vh;
    overflow-y: auto;
    gap: ${({ theme }) => theme.spacing[600]};
    background-color: ${({ theme }) => theme.colors.surface.primary};
    padding: 40px 60px;
    box-sizing: border-box;
    @media screen and (max-width: 400px) {
        width: 343px;
        justify-content: flex-start;
    }
    .block {
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[200]};
    }
    form,
    .auth-form {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing[400]};
    }
    .copyright {
        align-self: center;
    }
`
